import React, { useEffect, useRef, useState } from 'react';
import { useAnimatedCounter } from '../../../hooks/useAnimatedCounter';
import type { DashboardStats } from '../../../types/dashboard';

interface AccountSummaryProps {
  className?: string;
  stats?: DashboardStats;
}

const AccountSummary: React.FC<AccountSummaryProps> = ({
  className = '',
  stats = { totalTransactions: 1247, monthlyTransactions: 89 }
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const totalCounter = useAnimatedCounter({
    end: stats.totalTransactions,
    duration: 2500
  });

  const monthlyCounter = useAnimatedCounter({
    end: stats.monthlyTransactions,
    duration: 2000
  });

  // Intersection Observer for animation trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          totalCounter.startAnimation();
          monthlyCounter.startAnimation();
        }
      },
      { threshold: 0.5 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible, totalCounter, monthlyCounter]);

  return (
    <div ref={containerRef} className={`account-summary card-enhanced ${className}`}>
      <h2 className="section-title">
        <svg className="section-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
        Account Summary
      </h2>
      
      <div className="account-summary-content">
        <div className="summary-stat summary-stat-enhanced">
          <div className="summary-stat-label">Total Transactions</div>
          <div className="summary-stat-number summary-stat-number-enhanced">
            {totalCounter.value}
          </div>
        </div>
        
        <div className="summary-stat summary-stat-enhanced">
          <div className="summary-stat-label">This Month</div>
          <div className="summary-stat-number summary-stat-number-enhanced blue">
            {monthlyCounter.value}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSummary;